import type { CityDto, PastorDistrictDto } from "../../types/city";
import type { FormBuilderField } from "../../types/formBuilder";
import type { FormSubmissionDto } from "../../types/submission";
import { getSubmissionFieldValue } from "./normalizeSubmission";

export type CityPastorDistrictCoverageCity = {
  id: number;
  name: string;
  responseCount: number;
};

export type CityPastorDistrictCoverageGroup = {
  id: number;
  name: string;
  responseCount: number;
  filledCityCount: number;
  totalCityCount: number;
  cities: CityPastorDistrictCoverageCity[];
};

const collator = new Intl.Collator("ru", {
  numeric: true,
  sensitivity: "base",
});

function cityAnswerKey(raw: unknown): string {
  if (typeof raw === "string" || typeof raw === "number") {
    return String(raw).trim();
  }
  if (raw && typeof raw === "object" && "value" in raw) {
    const value = (raw as { value?: unknown }).value;
    if (typeof value === "string" || typeof value === "number") {
      return String(value).trim();
    }
  }
  return "";
}

function cityPastorDistrictId(city: CityDto): number | null {
  const value = city.pastorDistrictId ?? city.pastor_district_id;
  if (value == null) return null;
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}

export function buildCityPastorDistrictCoverage(
  submissions: FormSubmissionDto[],
  field: FormBuilderField,
  fields: FormBuilderField[],
  cities: CityDto[],
  pastorDistricts: PastorDistrictDto[],
): CityPastorDistrictCoverageGroup[] {
  const cityById = new Map(cities.map((city) => [String(city.id), city]));
  const cityByName = new Map(
    cities.map((city) => [city.name.trim().toLocaleLowerCase("ru-RU"), city]),
  );
  const responsesByCityId = new Map<number, number>();

  for (const submission of submissions) {
    const key = cityAnswerKey(
      getSubmissionFieldValue(submission.data, field, fields),
    );
    if (!key) continue;
    const city =
      cityById.get(key) ?? cityByName.get(key.toLocaleLowerCase("ru-RU"));
    if (!city) continue;
    responsesByCityId.set(city.id, (responsesByCityId.get(city.id) ?? 0) + 1);
  }

  const districtNames = new Map(
    pastorDistricts.map((district) => [district.id, district.districtName]),
  );
  const citiesByDistrict = new Map<number, CityDto[]>();

  for (const district of pastorDistricts) {
    citiesByDistrict.set(district.id, []);
  }
  for (const city of cities) {
    const districtId = cityPastorDistrictId(city);
    if (districtId == null) continue;
    const districtCities = citiesByDistrict.get(districtId);
    if (districtCities) districtCities.push(city);
    else {
      citiesByDistrict.set(districtId, [city]);
      districtNames.set(districtId, `ЕАР #${districtId}`);
    }
  }

  return [...citiesByDistrict.entries()].map(([districtId, districtCities]) => {
    const coverageCities = districtCities
      .map((city) => ({
        id: city.id,
        name: city.name,
        responseCount: responsesByCityId.get(city.id) ?? 0,
      }))
      .sort((left, right) => {
        if (right.responseCount !== left.responseCount) {
          return right.responseCount - left.responseCount;
        }
        return collator.compare(left.name, right.name);
      });
    const responseCount = coverageCities.reduce(
      (sum, city) => sum + city.responseCount,
      0,
    );

    return {
      id: districtId,
      name: districtNames.get(districtId) ?? `ЕАР #${districtId}`,
      responseCount,
      filledCityCount: coverageCities.filter((city) => city.responseCount > 0)
        .length,
      totalCityCount: coverageCities.length,
      cities: coverageCities,
    };
  });
}
